import { Button, Flex, Stack, StackProps } from '@mantine/core';
import { ReactNode, useCallback, useMemo } from 'react';

import { ListElement } from './ListElement';
import { ListItem } from './ListItem';

interface ListProps<T> extends Omit<StackProps, 'children' | 'onChange'> {
  items: T[];
  onChange: (items: T[]) => void;
  children: (item: T, index: number, helpers: ListItemHelpers<T>) => ReactNode;

  disabled?: boolean;
  addButtonLabel?: string;
  addButtonSize?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  addButtonVariant?: 'outline' | 'filled' | 'light' | 'subtle';
  createNewItem: () => T;
  spacing?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  'data-pw'?: string;
}

export interface ListItemHelpers<T> {
  update: (item: T) => void;
  remove: () => void;
  canRemove: boolean;
}

export const List = <T,>({
  items,
  onChange,
  children,
  disabled = false,
  addButtonLabel = 'Add',
  addButtonSize = 'lg',
  addButtonVariant = 'outline',
  createNewItem,
  'data-pw': dataPw,
  ...stackProps
}: ListProps<T>) => {
  const handleAdd = useCallback(() => {
    onChange([...items, createNewItem()]);
  }, [items, onChange, createNewItem]);

  const handleUpdate = useCallback(
    (index: number, item: T) => {
      const newItems = [...items];
      newItems[index] = item;
      onChange(newItems);
    },
    [items, onChange],
  );

  const handleRemove = useCallback(
    (index: number) => {
      const newItems = [...items];
      newItems.splice(index, 1);
      onChange(newItems);
    },
    [items, onChange],
  );

  const canRemove = items.length > 1;

  const itemHelpers = useMemo(
    () =>
      items.map((_, index) => ({
        update: (updatedItem: T) => handleUpdate(index, updatedItem),
        remove: () => handleRemove(index),
        canRemove,
      })),
    [items, handleUpdate, handleRemove, canRemove],
  );

  return (
    <Stack mt="xs" {...stackProps} data-pw={dataPw}>
      {items.map((item, index) => (
        <ListItem key={index} item={item} index={index} helpers={itemHelpers[index]}>
          {children}
        </ListItem>
      ))}

      {!disabled && (
        <Flex justify="flex-start">
          <Button onClick={handleAdd} variant={addButtonVariant} size={addButtonSize} data-pw={dataPw ? `${dataPw}-add` : undefined}>
            {addButtonLabel}
          </Button>
        </Flex>
      )}
    </Stack>
  );
};

List.Element = ListElement;
